import { useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import downloadIcon from '@/assets/icons/download.svg';
import arrowOutwardIcon from '@/assets/icons/arrow-outward.svg';
import type { PromoCodeDetailsResponse } from '@/services/promo-codes.service';
import { formatDateOnlyForDisplay } from '@/utils/date-only';
import styles from './AdminPromoCodeDetailsPanel.module.css';

type AdminPromoCodeDetailsPanelProps = {
  details: PromoCodeDetailsResponse;
  isStatusUpdating?: boolean;
  onClose: () => void;
  onOpenOrder: (orderId: number) => void;
  onChangeStatus: (promoCodeId: string, nextStatus: 'active' | 'inactive') => void | Promise<void>;
};

const formatDate = (value?: string | null) => {
  if (!value) {
    return '-';
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return '-';
  }

  return parsed.toLocaleString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
};

const formatDateOnly = (value?: string | null) =>
  formatDateOnlyForDisplay(value, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });

const formatMoney = (amount: number, currency: string) => `${(amount/100).toFixed(2)} ${currency}`.trim();

const formatUsageType = (oneTime: boolean) => (oneTime ? 'Single-use' : 'Multi-use');

const getPromoStatusLabel = (isRedeemed: boolean, active: boolean) => {
  if (isRedeemed) {
    return 'Redeemed';
  }

  return active ? 'Active' : 'Inactive';
};

const getPromoStatusClassName = (isRedeemed: boolean, active: boolean) =>
  isRedeemed ? styles.badgeRedeemed : active ? styles.badgeActive : styles.badgeInactive;

const normalizeOrderStatus = (value: string) => {
  const normalized = value.toLowerCase();
  const fallbackClassName = styles.statusDraft ?? '';

  if (!normalized) {
    return { label: '-', className: fallbackClassName };
  }

  const label = normalized.charAt(0).toUpperCase() + normalized.slice(1);

  const classNameByStatus: Record<string, string> = {
    draft: styles.statusDraft ?? '',
    created: styles.statusCreated ?? '',
    paid: styles.statusPaid ?? '',
    confirmed: styles.statusConfirmed ?? '',
    cancelled: styles.statusCancelled ?? '',
    refunded: styles.statusRefunded ?? '',
  };

  return {
    label,
    className: classNameByStatus[normalized] ?? fallbackClassName,
  };
};

const downloadPromoOrdersCsv = (details: PromoCodeDetailsResponse) => {
  const rows = details.orders.map((order, index) => {
    const orderTotalPreDiscount = order.clientPriceAmount + order.discountAmount;

    return [
      String(index + 1),
      String(order.orderId),
      formatMoney(order.discountAmount, order.clientPriceCurrency),
      formatMoney(orderTotalPreDiscount, order.clientPriceCurrency),
      order.status,
      formatDate(order.createdAt),
    ];
  });

  const header = ['#', 'Order ID', 'Discount', 'Order Total (pre-discount)', 'Order Status', 'Order Date'];
  const csvRows = [header, ...rows]
    .map((row) => row.map((cell) => `"${cell.replace(/"/g, '""')}"`).join(','))
    .join('\n');

  const blob = new Blob([csvRows], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = `promocode-${details.promoCode.code}-orders.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  URL.revokeObjectURL(url);
};

export const AdminPromoCodeDetailsPanel = ({
  details,
  isStatusUpdating,
  onClose,
  onOpenOrder,
  onChangeStatus,
}: AdminPromoCodeDetailsPanelProps) => {
  const handleEscape = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    },
    [onClose]
  );

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', handleEscape);
    };
  }, [handleEscape]);

  const promoCode = details.promoCode;

  return createPortal(
    <>
      <button
        type="button"
        className={styles.backdrop}
        aria-label="Close promo code details"
        onClick={onClose}
      />
      <aside
        className={styles.panel}
        role="dialog"
        aria-modal="true"
        aria-label={`Promo code ${promoCode.code} details`}
      >
        <div className={styles.header}>
          <div className={styles.meta}>ID: {promoCode.promoCodeId ?? '-'}</div>
          <div className={styles.headingRow}>
            <div className={styles.headingWrap}>
              <h2 className={styles.title}>
                {promoCode.code}
              </h2>
              {promoCode.isRedeemed ? (
                <span className={`${styles.badge} ${getPromoStatusClassName(promoCode.isRedeemed, promoCode.active)}`}>
                  {getPromoStatusLabel(promoCode.isRedeemed, promoCode.active)}
                </span>
              ) : (
                <label
                  className={`${styles.statusToggle} ${
                    promoCode.active ? styles.statusToggleActive : styles.statusToggleInactive
                  }`}
                >
                  <select
                    className={styles.statusSelect}
                    value={promoCode.active ? 'active' : 'inactive'}
                    disabled={Boolean(isStatusUpdating)}
                    onChange={(event) => {
                      const nextStatus = event.target.value === 'active' ? 'active' : 'inactive';
                      void onChangeStatus(promoCode.id, nextStatus);
                    }}
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                  <span className={styles.statusChevron}>⌄</span>
                </label>
              )}
            </div>
            <div className={styles.actions}>
              <button
                type="button"
                className={styles.primaryButton}
                onClick={() => downloadPromoOrdersCsv(details)}
              >
                <img src={downloadIcon} alt="Download" />
                Export
              </button>
              <button
                type="button"
                className={styles.closeButton}
                aria-label="Close promo code panel"
                onClick={onClose}
              >
                ×
              </button>
            </div>
          </div>
        </div>

        <section className={styles.section}>
          <div className={styles.infoGrid}>
            <div className={styles.field}>
              <span className={styles.fieldLabel}>Usage Type</span>
              <span className={styles.fieldValue}>{formatUsageType(promoCode.oneTime)}</span>
            </div>
            <div className={styles.field}>
              <span className={styles.fieldLabel}>Times Used</span>
              <span className={styles.fieldValue}>{promoCode.timesUsed ?? 0}</span>
            </div>
            <div className={styles.field}>
              <span className={styles.fieldLabel}>Validity</span>
              <span className={styles.fieldValue}>
                {`${formatDateOnly(promoCode.startDate)}-${formatDateOnly(promoCode.endDate)}`}
              </span>
            </div>
            <div className={styles.field}>
              <span className={styles.fieldLabel}>Min. Order</span>
              <span className={styles.fieldValue}>
                {promoCode.minOrderAmount != null ? formatMoney(promoCode.minOrderAmount, 'EUR') : '-'}
              </span>
            </div>
          </div>
        </section>

        <section className={styles.section}>
          <h3 className={styles.sectionTitle}>Orders</h3>
          <div className={styles.tableWrapper}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th className={styles.tableHead}>#</th>
                  <th className={styles.tableHead}>Order ID</th>
                  <th className={styles.tableHead}>Discount</th>
                  <th className={styles.tableHead}>Order Total (pre-discount)</th>
                  <th className={styles.tableHead}>Order Status</th>
                  <th className={styles.tableHead}>Order Date</th>
                  <th className={styles.tableHead} />
                </tr>
              </thead>
              <tbody>
                {details.orders.map((order, index) => {
                  const orderStatus = normalizeOrderStatus(order.status);
                  const orderTotalPreDiscount = order.clientPriceAmount + order.discountAmount;

                  return (
                    <tr key={`${promoCode.id}-order-${order.orderId}-${index}`}>
                      <td className={`${styles.tableCell} ${styles.smallCell}`}>{index + 1}</td>
                      <td className={`${styles.tableCell} ${styles.smallCell}`}>{order.reference || order.orderId}</td>
                      <td className={`${styles.tableCell} ${styles.smallCell}`}>
                        {formatMoney(order.discountAmount, order.clientPriceCurrency)}
                      </td>
                      <td className={`${styles.tableCell} ${styles.smallCell}`}>
                        {formatMoney(orderTotalPreDiscount, order.clientPriceCurrency)}
                      </td>
                      <td className={`${styles.tableCell} ${styles.smallCell}`}>
                        <span className={`${styles.statusPill} ${orderStatus.className}`}>{orderStatus.label}</span>
                      </td>
                      <td className={`${styles.tableCell} ${styles.smallCell}`}>{formatDate(order.createdAt)}</td>
                      <td className={`${styles.tableCell} ${styles.smallCell}`}>
                        <button
                          type="button"
                          className={styles.orderLinkButton}
                          aria-label={`Open order ${String(order.orderId)} details`}
                          onClick={() => onOpenOrder(order.orderId)}
                        >
                          <img src={arrowOutwardIcon} alt="" aria-hidden className={styles.orderLinkIcon} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>

        <section className={styles.section}>
          <h3 className={styles.sectionTitle}>Activity Log</h3>
          <div className={styles.logCard}>
            <div className={`${styles.field} ${styles.activityRow}`}>
              <span className={styles.activityIcon}>⏲</span>
              <div>
                <span className={styles.fieldLabel}>Created At</span>
                <span className={styles.fieldValue}>
                  {formatDate(promoCode.createdAt)} <span className={styles.muted}>by {promoCode.createdByAdmin}</span>
                </span>
              </div>
            </div>
          </div>
        </section>
      </aside>
    </>,
    document.body
  );
};
